import { useRouter, useRouterState } from '@tanstack/react-router';

import { type MovedPage, movedPagesOf } from '../movedPages';
import { routeNameOf } from '../routeName';
import type { AppLinkOptions } from './linkOptions';

/** A nav entry: the page it links to, and the pages it stands for when more than that one and those beneath it. */
type NavEntry = { link: AppLinkOptions; activeFor?: ReadonlyArray<AppLinkOptions> };

/**
 * For a sidebar of nav entries, such as `AppSidebar`: of `entries`, the narrowest whose pages hold the page on screen (Supplier strategy, not
 * Suppliers, whose route it extends), and whether the page is its link's exactly. Entries link to today's routes, so a
 * page with a second route (`createAppRouter`'s `movedPages`) is looked up as the route it moved from.
 */
export function useCurrentEntry(entries: ReadonlyArray<NavEntry>): { index: number; exact: boolean } | undefined {
    const router = useRouter();
    return useRouterState({
        // `matches` are the page on screen's until the next page renders, as `matchRoute` reads the location.
        select: ({ matches }) => {
            const moved = movedPagesOf(router).find(({ to }) => to === routeNameOf(router, matches));
            const params: Record<string, string> = matches.at(-1)?.params ?? {};
            const holdsPage = (link: AppLinkOptions, exact: boolean): boolean =>
                moved
                    ? isMovedPageAt(moved, params, link, exact)
                    : Boolean(router.matchRoute(link as never, { fuzzy: !exact, includeSearch: false }));

            let current: { index: number; length: number } | undefined;
            entries.forEach(({ link, activeFor = [link] }, index) => {
                for (const scope of activeFor) {
                    // The longer the path the entry stands for, the narrower the entry.
                    const length = holdsPage(scope, false) ? router.buildLocation(scope as never).pathname.length : -1;
                    if (length > (current?.length ?? -1)) {
                        current = { index, length };
                    }
                }
            });
            return current && { index: current.index, exact: holdsPage(entries[current.index].link, true) };
        },
        // A new object each run would re-render the sidebar on every change of the router's state.
        structuralSharing: true,
    });
}

/** Whether a moved page, on screen with `params` under its new route, is at `link`'s route or, unless `exact`, below it. */
function isMovedPageAt(
    moved: MovedPage,
    params: Record<string, string>,
    link: AppLinkOptions,
    exact: boolean,
): boolean {
    const { to, params: linkParams } = link as { to?: string; params?: unknown };
    if (to !== moved.from && (exact || to === undefined || !moved.from.startsWith(`${to}/`))) {
        return false;
    }
    // Both routes name their params alike. A route above the page has fewer: those it lacks aren't compared.
    const expected = typeof linkParams === 'object' && linkParams !== null ? linkParams : {};
    return Object.entries(expected).every(([name, value]) => value === undefined || params[name] === value);
}
